const PILOT_STATUS_STORAGE_KEY = 'wingit_pilot-status-v1';

function toDateValue(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date;
}

function formatDate(value) {
  const date = toDateValue(value);
  if (!date) return '-';
  return date.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}

function daysUntil(value) {
  const date = toDateValue(value);
  if (!date) return null;
  const today = new Date();
  const midnightToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const midnightTarget = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffMs = midnightTarget.getTime() - midnightToday.getTime();
  return Math.ceil(diffMs / (24 * 60 * 60 * 1000));
}

function statusFromDays(daysRemaining, warningDays) {
  if (daysRemaining === null) return 'red';
  if (daysRemaining < 0) return 'red';
  if (daysRemaining <= warningDays) return 'amber';
  return 'green';
}

function loadStatusStore() {
  try {
    const raw = localStorage.getItem(PILOT_STATUS_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (_err) {
    return {};
  }
}

function saveStatusStore(store) {
  localStorage.setItem(PILOT_STATUS_STORAGE_KEY, JSON.stringify(store || {}));
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('wingit-pilot-status-updated'));
  }
}

export function loadPilotCompliance(userId) {
  if (!userId) return {};
  const store = loadStatusStore();
  const payload = store[userId];
  return payload && typeof payload === 'object' ? payload : {};
}

export function savePilotCompliance(userId, payload) {
  if (!userId) return;
  const store = loadStatusStore();
  store[userId] = {
    ...(payload || {}),
    updatedAt: new Date().toISOString(),
  };
  saveStatusStore(store);
}

export function buildPilotStatusSummary(compliance = {}, operationalSettings = {}) {
  const medicalWarningDays = Number(operationalSettings.medicalWarningDays) || 30;
  const currencyWarningDays = Number(operationalSettings.currencyWarningDays) || 7;
  const checkFlightWarningDays = Number(operationalSettings.checkFlightWarningDays) || 14;
  const clubCurrencyWindowDays = Number(operationalSettings.clubCurrencyWindowDays) || 60;
  const nightCurrencyEnabled = operationalSettings.nightCurrencyEnabled !== false;
  const checkFlightEnabled = operationalSettings.checkFlightEnabled !== false;

  const clubCurrencyExpiry = compliance.lastLandingDate
    ? new Date(new Date(compliance.lastLandingDate).getTime() + clubCurrencyWindowDays * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)
    : null;

  const licenceDays = daysUntil(compliance.licenceExpiry);
  const medicalDays = daysUntil(compliance.medicalExpiry);
  const clubCurrencyDays = daysUntil(clubCurrencyExpiry);
  const nightDays = daysUntil(compliance.nightCurrencyExpiry);
  const checkFlightDays = daysUntil(compliance.checkFlightDue);

  const rows = [
    {
      key: 'licence',
      label: 'Licence',
      status: statusFromDays(licenceDays, medicalWarningDays),
      summary: licenceDays === null ? 'No expiry recorded' : licenceDays < 0 ? `Expired ${Math.abs(licenceDays)} day(s) ago` : `Expires in ${licenceDays} day(s)`,
      dateLabel: formatDate(compliance.licenceExpiry),
    },
    {
      key: 'medical',
      label: 'Medical',
      status: statusFromDays(medicalDays, medicalWarningDays),
      summary: medicalDays === null ? 'No expiry recorded' : medicalDays < 0 ? `Expired ${Math.abs(medicalDays)} day(s) ago` : `Expires in ${medicalDays} day(s)`,
      dateLabel: formatDate(compliance.medicalExpiry),
    },
    {
      key: 'clubCurrency',
      label: 'Club Currency',
      status: statusFromDays(clubCurrencyDays, currencyWarningDays),
      summary: clubCurrencyDays === null ? 'No landing data' : clubCurrencyDays < 0 ? `Expired ${Math.abs(clubCurrencyDays)} day(s) ago` : `${clubCurrencyDays} day(s) remaining`,
      dateLabel: formatDate(clubCurrencyExpiry),
    },
  ];

  if (nightCurrencyEnabled) {
    rows.push({
      key: 'nightCurrency',
      label: 'Night Currency',
      status: statusFromDays(nightDays, currencyWarningDays),
      summary: nightDays === null ? 'No night record' : nightDays < 0 ? `Expired ${Math.abs(nightDays)} day(s) ago` : `${nightDays} day(s) remaining`,
      dateLabel: formatDate(compliance.nightCurrencyExpiry),
    });
  }

  if (checkFlightEnabled) {
    rows.push({
      key: 'checkFlight',
      label: 'Check Flight',
      status: statusFromDays(checkFlightDays, checkFlightWarningDays),
      summary: checkFlightDays === null ? 'No due date set' : checkFlightDays < 0 ? `Overdue by ${Math.abs(checkFlightDays)} day(s)` : `Due in ${checkFlightDays} day(s)`,
      dateLabel: formatDate(compliance.checkFlightDue),
    });
  }

  const blockingRows = rows.filter(row => row.status === 'red');
  const warningRows = rows.filter(row => row.status === 'amber');

  return {
    rows,
    hasBlocking: blockingRows.length > 0,
    hasWarning: warningRows.length > 0,
    blockingRows,
    warningRows,
  };
}
